import {Offer, ProductReference, Quote} from '.';
import {FactoryFn} from './factory-function';
import {Image} from './image';
import {ProductComponentModel} from './product-component-model';
import {Bundle, Product} from '@sportngin/se-commerce-sdk/models/clare';
import {pluckClarePrice} from './clare-helpers';

function getClareUuid(source: Product | Bundle): string | undefined {
  return 'uuid' in source ? source.uuid : undefined;
}

function getDefaultVariationId(source: Product | Bundle): number | undefined {
  return 'default_variation_id' in source
    ? source.default_variation_id
    : undefined;
}

export const serviceOfferComponentKey = 'service-offer';

const itemToServiceOffer = (
  source: Product | Bundle,
  quote?: Quote,
  ref?: ProductReference,
): ServiceOffer => {
  return new ServiceOffer({
    categoryId: null,
    description: source.description || '',
    headline: source.display_metadata?.headline ?? '',
    id: source.id ? String(source.id) : '',
    name: source.name || '',
    price: pluckClarePrice(source),
    selected: false,
    tagline: source.display_metadata?.tagline ?? '',
    disclaimer: source.display_metadata?.disclaimer ?? '',
    inputLabels: source.display_metadata?.input_labels || [],
    images: source.display_metadata?.images ?? [],
    logo: source.display_metadata?.logo,
    quoteProvider: source.display_metadata?.quote_provider ?? undefined,
    quote: quote,
    references: ref,
    marketingLabel: source.display_metadata?.marketing_label ?? '',
    marketingUrl: source.display_metadata?.marketing_url ?? '',
    marketingDisclaimer: source.display_metadata?.marketing_disclaimer ?? '',
    clareUuid: getClareUuid(source),
    templateKey:
      source.display_metadata?.template_key ?? serviceOfferComponentKey,
    itemVariationId: getDefaultVariationId(source),
    fulfillment: source.display_metadata?.fulfilled_by,
  });
};

const itemToProductComponentModelServiceOffer: FactoryFn<
  Product | Bundle,
  ProductComponentModel<ServiceOffer>
> = (
  source: Product | Bundle,
  quote?: Quote,
  ref?: ProductReference,
  catalogKey?: string,
) => {
  return new ProductComponentModel<ServiceOffer>(
    source.display_metadata?.template_key ?? serviceOfferComponentKey,
    itemToServiceOffer(source, quote, ref),
    source.display_metadata?.title ?? '',
    source.display_metadata?.subtitle ?? '',
    true,
    catalogKey ??
      (source.catalog_id !== undefined ? String(source.catalog_id) : undefined),
  );
};

class ServiceOffer implements Offer {
  categoryId: string | null;
  description: string;
  disclaimer: string;
  disclaimerUrl: string;
  headline: string;
  id: string;
  images: Image[];
  inputLabels: string[];
  name: string;
  price: number;
  selected: boolean;
  subtitle: string;
  tagline: string;
  title: string;
  logo?: Image;
  quoteProvider?: string;
  quote?: Quote;
  references?: ProductReference;
  maxQuantity: number | null;
  clareUuid?: string | null;
  templateKey: string;
  taxCode?: number;
  itemVariationId?: number;
  fulfillment?: string | null;
  marketingLabel?: string;
  marketingUrl?: string;
  marketingDisclaimer?: string;
  constructor(source?: Offer) {
    if (!source) return;

    this.categoryId = source.categoryId;
    this.description = source.description;
    this.disclaimer = source.disclaimer;
    this.headline = source.headline;
    this.id = source.id;
    this.images = [...source.images];
    this.name = source.name;
    this.price = source.price;
    this.selected = source.selected;
    this.tagline = source.tagline;
    this.inputLabels = source.inputLabels;
    this.logo = source.logo;
    this.quoteProvider = source.quoteProvider;
    this.quote = source.quote;
    this.references = source.references;
    this.maxQuantity = source.maxQuantity || null;
    this.clareUuid = source.clareUuid || null;
    this.templateKey = source.templateKey;
    this.taxCode = source.taxCode;
    this.itemVariationId = source.itemVariationId;
    this.fulfillment = source.fulfillment;
    this.marketingLabel = source.marketingLabel;
    this.marketingUrl = source.marketingUrl;
    this.marketingDisclaimer = source.marketingDisclaimer;
  }

  get image(): Image {
    return this.images[0] || this.logo;
  }

  quoteRequired(): boolean {
    return !!this.quoteProvider;
  }
}

/**
 * Checks for  templateKey with the value 'service-offer' to determine if the product is a service offer.
 * @param addOn the source object to be tested.
 */
function isOffer(addOn: {
  templateKey?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  template_key?: string;
}): addOn is Offer {
  return (
    addOn.templateKey === serviceOfferComponentKey ||
    addOn.template_key === serviceOfferComponentKey
  );
}

export {
  isOffer,
  ServiceOffer,
  itemToServiceOffer,
  itemToProductComponentModelServiceOffer,
};
